import { toPng } from 'html-to-image';
import { stripPortalScripts } from './stripPortalScripts';

const DEFAULT_WIDTH = 780;
const DEFAULT_HEIGHT = 1213; // ~9:14 for tall card previews

/**
 * Render portal shell HTML in an offscreen iframe and return a PNG Blob
 * of the first viewport (portrait page preview).
 */
export async function capturePortalHtmlAsPng(
  html,
  { width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT } = {}
) {
  if (!html) throw new Error('No HTML to capture');

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = [
    'position:fixed',
    'left:-10000px',
    'top:0',
    `width:${width}px`,
    `height:${height}px`,
    'border:0',
    'opacity:0',
    'pointer-events:none',
    'background:#fff',
  ].join(';');
  document.body.appendChild(iframe);

  try {
    await new Promise((resolve, reject) => {
      const timer = setTimeout(resolve, 4000);
      iframe.onload = () => {
        clearTimeout(timer);
        resolve();
      };
      iframe.onerror = err => {
        clearTimeout(timer);
        reject(err);
      };
      iframe.srcdoc = stripPortalScripts(html);
    });

    // Allow fonts / images / layout to settle
    await new Promise(r => setTimeout(r, 500));

    const doc = iframe.contentDocument;
    const target = doc?.body || doc?.documentElement;
    if (!target) throw new Error('Preview iframe has no document');

    // Constrain capture to the first viewport
    const prevOverflow = doc.documentElement.style.overflow;
    doc.documentElement.style.overflow = 'hidden';
    doc.body.style.margin = '0';
    doc.body.style.width = `${width}px`;
    doc.body.style.minHeight = `${height}px`;

    const dataUrl = await toPng(target, {
      width,
      height,
      canvasWidth: width,
      canvasHeight: height,
      pixelRatio: 1.25,
      backgroundColor: '#ffffff',
      // Avoid CORS failures embedding remote stylesheets/fonts
      fontEmbedCSS: '',
      cacheBust: true,
      style: {
        width: `${width}px`,
        height: `${height}px`,
        overflow: 'hidden',
        transform: 'none',
      },
    });

    doc.documentElement.style.overflow = prevOverflow;

    const response = await fetch(dataUrl);
    return response.blob();
  } finally {
    iframe.remove();
  }
}
